import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { userService } from '../services/userService';

// F1/F2 (Workstream F items 1/2): the ONE affordance for an admin to change
// another user's password, shared by the Users and Employees pages. It calls
// the proven admin reset route (POST /api/users/:id/reset-password), which
// hashes once, clears the account's login backoff, revokes the user's other
// sessions, writes the RESET_PASSWORD audit row and bells the user.
//
// Deliberately NOT a field on the edit forms: the 2026-09-03 lockout was a
// "New Password" field the profile PUT silently ignored while reporting
// success. A reset is its own act, with its own confirmation and its own toast.
//
// Auth Phase 2 (P2-A, ruling R8): the admin-typed password is a ONE-TIME
// credential (§8b). The server leaves password_updated_at NULL, so the user is
// forced to set their own password at their next v2 sign-in (D1). Policy is
// 12–72 + breach check, enforced server-side; MIN_LENGTH here only mirrors it.
const MIN_LENGTH = 12;

const ResetPasswordModal = ({ user, isSelf = false, onClose }) => {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const valid = password.length >= MIN_LENGTH && confirm === password && !busy;
  const label = user?.name || user?.email || 'this user';

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    try {
      setBusy(true);
      const res = await userService.resetPassword(user.id, password);
      const revoked = Number(res?.sessions_revoked) || 0;
      toast.success(
        `Password reset for ${label}.` +
        (revoked > 0 ? ` ${revoked} other session${revoked === 1 ? '' : 's'} signed out.` : '')
      );
      onClose();
    } catch (err) {
      // The fetch client throws Error(server message) on 400/403/404.
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !busy && onClose()}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h2>Reset password</h2>
        <p style={{ marginTop: 4 }}>
          Set a new password for <strong>{label}</strong>
          {user?.email && user.name ? <span style={{ opacity: 0.7 }}> ({user.email})</span> : null}.
        </p>
        <form onSubmit={submit} autoComplete="off">
          <div className="form-group">
            <label>New password *</label>
            <input
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
              disabled={busy}
            />
            {tooShort && <small className="form-hint">At least {MIN_LENGTH} characters.</small>}
          </div>
          <div className="form-group">
            <label>Confirm new password *</label>
            <input
              type={show ? 'text' : 'password'}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={busy}
            />
            {mismatch && <small className="form-hint">Passwords don't match.</small>}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 12 }}>
            <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} disabled={busy} />
            Show password
          </label>
          <p style={{ fontSize: 13, opacity: 0.8, marginTop: 0 }}>
            {isSelf
              ? 'Your other sessions will be signed out; this one stays signed in.'
              : 'All of their sessions will be signed out, and they will see a notice on their next sign-in.'}
            {' '}{isSelf
              ? "You'll be required to set a new password when your session next refreshes."
              : "They'll be required to set a new password at their next sign-in."}
          </p>
          <div className="modal-actions">
            <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={!valid}>
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
